"use client";
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { CircleCheck as CheckCircle, X, Search, ShieldCheck, Clock, Headphones, SendHorizonal, Info, User } from 'lucide-react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { submitQuickInquiry, validateSkus, searchSkus } from '@/lib/api'
import type { ProductVariant } from '@/types'

const schema = z.object({
  customer_name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email required'),
  company: z.string().optional(),
  country: z.string().optional(),
  message: z.string().min(5, 'Please describe your requirement'),
})
type FormData = z.infer<typeof schema>

const COUNTRIES = [
  'United States', 'United Kingdom', 'United Arab Emirates', 'Germany', 'France',
  'Australia', 'Canada', 'Singapore', 'South Africa', 'Kenya', 'Nigeria',
  'Saudi Arabia', 'Qatar', 'Kuwait', 'Oman', 'Bahrain', 'Netherlands',
  'Belgium', 'Italy', 'Spain', 'Japan', 'South Korea', 'New Zealand', 'Other',
]

export function QuickInquiryPage() {
  const searchParams = useSearchParams()
  const initialSku = searchParams?.get('sku') || ''
  const [skuInput, setSkuInput] = useState(initialSku)
  const [validatedSkus, setValidatedSkus] = useState<ProductVariant[]>([])
  const [invalidSkus, setInvalidSkus] = useState<string[]>([])
  const [validating, setValidating] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [countryValue, setCountryValue] = useState('')
  const [suggestions, setSuggestions] = useState<{sku: string, productName: string, id: string}[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)

  // Debounced search for SKU suggestions
  useEffect(() => {
    const query = skuInput.trim()
    if (!query || query.includes(',')) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const results = await searchSkus(query)
        setSuggestions(results)
        setShowSuggestions(true)
      } catch (err) {
        console.error("Failed to fetch SKU suggestions", err)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [skuInput])

  async function handleSelectSuggestion(sku: string) {
    setSkuInput('')
    setShowSuggestions(false)
    setValidating(true)
    const found = await validateSkus([sku])
    if (found.length) {
      setValidatedSkus([found[0]])
    }
    setValidating(false)
  }

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  async function handleValidateSkus() {
    const skus = skuInput
      .split(/[\n,\s]+/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean)
    if (!skus.length) return
    setValidating(true)
    const found = await validateSkus([skus[0]]) // Only take the first one for single-select
    setValidatedSkus(found)
    const foundSkus = new Set(found.map((v) => v.sku))
    setInvalidSkus(!foundSkus.has(skus[0]) ? [skus[0]] : [])
    setValidating(false)
  }

  async function onSubmit(data: FormData) {
    setSubmitting(true)
    try {
      await submitQuickInquiry({
        customer_name: data.customer_name,
        email: data.email,
        company: data.company ?? '',
        country: data.country ?? '',
        product_sku: validatedSkus.map((v) => v.sku).join(', '),
        message: data.message,
      })
      setSubmitted(true)
    } catch {
      toast.error('Failed to submit inquiry. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#fcf2e3]">
          <CheckCircle className="h-8 w-8 text-[#8b5e34]" />
        </div>
        <h1 className="text-2xl font-bold mb-3">Inquiry Sent!</h1>
        <p className="text-muted-foreground mb-8">
          We'll respond within 1 business day with availability and pricing details.
        </p>
        <div className="flex gap-3 justify-center">
          <Link href="/"><Button className="bg-primary text-primary-foreground hover:bg-primary/90">Back to Home</Button></Link>
          <Link href="/quote"><Button variant="outline">Request a Quote</Button></Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          '@id': 'https://jamnagarmetals.com/quick-inquiry',
          name: 'Quick Inquiry | Jamnagar Metals',
          url: 'https://jamnagarmetals.com/quick-inquiry',
          description: 'Fast inquiry for specific SKUs or general product questions. Guaranteed response within 1 business day.',
          publisher: { '@id': 'https://jamnagarmetals.com/#organization' },
        })
      }} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2">Quick Inquiry</h1>
          <p className="text-muted-foreground">
            Enter SKU codes or describe your requirement. We'll respond within{' '}
            <span className="font-semibold text-foreground">1 business day</span>.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">

          {/* SKU Lookup Card */}
          <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-11 h-11 rounded-xl bg-[#fcf2e3] flex items-center justify-center shrink-0">
                <Search className="w-5 h-5 text-[#8b5e34]" />
              </div>
              <div>
                <h2 className="text-[17px] font-bold tracking-tight">SKU Lookup (optional)</h2>
                <p className="text-sm text-muted-foreground mt-0.5">Enter SKU codes to quickly check availability and get faster quotes.</p>
              </div>
            </div>

            <div className="mb-3 relative">
              <Label htmlFor="skus" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-2">
                Search SKU Codes <Info className="w-3.5 h-3.5" />
              </Label>
              <div className="relative">
                <Input
                  id="skus"
                  className="font-mono text-sm h-11"
                  placeholder="e.g. BB-4IN-SS-SAT (type to search)"
                  value={skuInput}
                  onChange={(e) => setSkuInput(e.target.value)}
                  onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true) }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  autoComplete="off"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* Suggestions Dropdown */}
              {showSuggestions && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-xl shadow-lg overflow-hidden flex flex-col max-h-[250px] overflow-y-auto custom-scrollbar">
                  {suggestions.length > 0 ? (
                    suggestions.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="flex flex-col text-left px-4 py-2.5 hover:bg-brand-accent/5 transition-colors border-b border-border/50 last:border-0"
                        onClick={() => handleSelectSuggestion(s.sku)}
                      >
                        <span className="font-mono text-[13px] font-bold text-brand-accent">{s.sku}</span>
                        <span className="text-[12px] text-muted-foreground line-clamp-1">{s.productName}</span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                      No matching SKUs found
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full border-border text-[#8b5e34] font-semibold gap-2 hover:bg-[#fcf2e3]/50"
              onClick={handleValidateSkus}
              disabled={validating || !skuInput.trim()}
            >
              <ShieldCheck className="w-4 h-4" />
              {validating ? 'Validating...' : 'Validate SKUs'}
            </Button>

            {validatedSkus.length > 0 && (
              <div className="mt-4 flex flex-col gap-2">
                <p className="text-sm font-semibold text-green-700">Valid SKUs found:</p>
                <div className="flex flex-wrap gap-2">
                  {validatedSkus.map((v) => (
                    <Badge key={v.id} className="text-xs gap-1.5 px-3 py-1 font-mono font-semibold bg-[#8b5e34] text-white border-0 rounded-full">
                      {v.sku}
                      <button
                        type="button"
                        onClick={() => setValidatedSkus((prev) => prev.filter((x) => x.id !== v.id))}
                        className="hover:opacity-70"
                        aria-label={`Remove ${v.sku}`}
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {invalidSkus.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-destructive mb-1">SKUs not found:</p>
                <div className="flex flex-wrap gap-2 mb-1">
                  {invalidSkus.map((s) => (
                    <Badge key={s} variant="destructive" className="text-xs">{s}</Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  These SKUs were not found in our catalogue. You can still submit your inquiry and we'll assist you.
                </p>
              </div>
            )}
          </div>

          {/* Your Details Card */}
          <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-11 h-11 rounded-xl bg-[#fcf2e3] flex items-center justify-center shrink-0">
                <User className="w-5 h-5 text-[#8b5e34]" />
              </div>
              <div>
                <h2 className="text-[17px] font-bold tracking-tight">Your Details</h2>
                <p className="text-sm text-muted-foreground mt-0.5">Please provide your details so our team can get back to you.</p>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="qi-name" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
                    Name <span className="text-red-500">*</span>
                  </Label>
                  <Input id="qi-name" {...register('customer_name')} placeholder="Full name" />
                  {errors.customer_name && <p className="text-xs text-destructive mt-1">{errors.customer_name.message}</p>}
                </div>
                <div>
                  <Label htmlFor="qi-email" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input id="qi-email" type="email" {...register('email')} placeholder="you@company.com" />
                  {errors.email && <p className="text-xs text-destructive mt-1">{errors.email.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="qi-company" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2 block">
                    Company
                  </Label>
                  <Input id="qi-company" {...register('company')} placeholder="Optional" />
                </div>
                <div>
                  <Label htmlFor="qi-country" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2 block">
                    Destination Country
                  </Label>
                  <Select
                    value={countryValue}
                    onValueChange={(val) => {
                      setCountryValue(val)
                      setValue('country', val)
                    }}
                  >
                    <SelectTrigger id="qi-country" className="w-full">
                      <SelectValue placeholder="Optional" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="max-h-[220px]">
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="qi-message" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
                  Message / Requirement <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="qi-message"
                  {...register('message')}
                  rows={4}
                  placeholder="Describe your product requirements, quantities, or specific questions..."
                />
                {errors.message && <p className="text-xs text-destructive mt-1">{errors.message.message}</p>}
              </div>

              {/* Security note */}
              <div className="flex items-center gap-2 text-sm bg-[#fcf2e3]/60 border border-[#e8d5bc] rounded-lg px-4 py-3">
                <ShieldCheck className="w-4 h-4 text-[#8b5e34] shrink-0" />
                <span><span className="font-semibold text-[#8b5e34]">Your information is secure.</span> We will never share your details with third parties.</span>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 text-base font-semibold bg-[#0f172a] hover:bg-[#0f172a]/90 text-white gap-2"
              >
                <SendHorizonal className="w-4 h-4 -rotate-45" />
                {submitting ? 'Sending...' : 'Send Inquiry'}
              </Button>

              {/* Trust badges */}
              <div className="grid grid-cols-3 divide-x divide-border pt-2">
                {[
                  { icon: Clock, label: 'Response within', value: '1 business day' },
                  { icon: ShieldCheck, label: 'Secure &', value: 'Confidential' },
                  { icon: Headphones, label: 'Expert support', value: 'Guaranteed' },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={value} className="flex items-center justify-center gap-2.5 px-3 py-3">
                    <Icon className="w-5 h-5 text-[#8b5e34] shrink-0" />
                    <div className="text-left">
                      <span className="block text-[11px] text-muted-foreground leading-tight">{label}</span>
                      <span className="block text-[12px] font-bold leading-tight">{value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </form>
      </div>
    </>
  )
}
